import {
  Component,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { UntypedFormControl } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  EntitySearchHit,
  SearchService,
} from 'src/app/shared/services/metadata-api/search.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import { ConfigService } from 'src/app/service/config.service';
import {
  AiSearchService,
  truncateDescription,
} from 'src/app/shared/services/ai-search/ai-search.service';
import { AiSearchRecentsService } from 'src/app/shared/services/ai-search/ai-search-recents.service';
import {
  CommodityQueryRequest,
  EntityType,
  LexiconResponse,
  QueryFilter,
  TopicQueryRequest,
  YukkaQuery,
} from 'src/app/shared/models/ai-search.interface';
import { Entity } from 'src/app/shared/models/entity.interface';
import {
  CompanyFormComponent,
  CompanyFormData,
} from './company-form/company-form.component';

type DialogStep = 'select-type' | 'configure' | 'loading';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-ai-search-dialog',
  templateUrl: './ai-search-dialog.component.html',
  styleUrls: ['./ai-search-dialog.component.scss'],
})
export class AiSearchDialogComponent implements OnDestroy {
  step: DialogStep = 'select-type';
  errorMessage = '';

  entityType: EntityType = 'company';
  searchTerm = '';
  userEmail = '';
  project = '';

  companySearchEnabled = false;
  companyEnriching = false;
  loadingSubject = '';
  private generationSub: Subscription | null = null;
  @ViewChild(CompanyFormComponent) companyFormRef: CompanyFormComponent;
  private pendingCompanyData: CompanyFormData | null = null;

  // Topic fields
  topic = '';
  entityControl = new UntypedFormControl('');
  entityValue = '';
  entityResults$: Observable<EntitySearchHit[]>;

  // Commodity fields
  commodity = '';

  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<AiSearchDialogComponent>,
    private searchService: SearchService,
    private userInfo: UserInfoService,
    private config: ConfigService,
    private aiSearchService: AiSearchService,
    private aiRecentsService: AiSearchRecentsService,
  ) {
    this.searchTerm = this.aiSearchService.pendingLegalName ?? '';
    this.aiSearchService.pendingLegalName = null;
    this.userEmail = this.userInfo.email || '';
    this.project = this.config.appConfig.routing.project;
    this.setupEntitySearch();
  }

  private setupEntitySearch(): void {
    this.entityResults$ = this.entityControl.valueChanges.pipe(
      // Clear previous selection as soon as the user types again
      tap((value) => {
        if (typeof value === 'string') this.entityValue = '';
      }),
      debounceTime(500),
      distinctUntilChanged(),
      // The same search the company form picks from, so both dropdowns show the
      // same columns — country, and the article count over the period.
      switchMap((value) =>
        typeof value === 'string' && value.trim().length >= 2
          ? this.searchService.search(value, false).pipe(
              map((res: { hits?: EntitySearchHit[] }) => res.hits || []),
              catchError(() => of([] as EntitySearchHit[])),
            )
          : of([] as EntitySearchHit[]),
      ),
    );
  }

  displayEntity(option: unknown): string {
    if (!option || typeof option === 'string') return (option as string) || '';
    return (option as EntitySearchHit).entity?.name ?? '';
  }

  selectEntity(option: { entity: { compound_key: string } }): void {
    this.entityValue = option.entity.compound_key;
  }

  // Text that never resolved to an entity is dropped when the user leaves the
  // field. Clicking an option blurs the input before Material emits the
  // selection, so a blur with the panel still open is not the user leaving —
  // the panel's own `closed` covers that, by which time the pick has landed.
  onEntityBlur(panelOpen = false): void {
    if (panelOpen) {
      return;
    }
    if (typeof this.entityControl.value === 'string') {
      this.entityControl.setValue('');
      this.entityValue = '';
    }
  }

  selectType(type: EntityType): void {
    this.entityType = type;
  }

  goBack(): void {
    this.step = 'select-type';
    this.dialogRef.updateSize('');
  }

  continueToForm(): void {
    this.step = 'configure';
    if (this.entityType === 'company') {
      this.dialogRef.updateSize('500px');
    } else {
      this.dialogRef.updateSize('');
    }
    if (this.entityType === 'topic' && !this.topic && this.searchTerm) {
      this.topic = this.searchTerm;
    } else if (
      this.entityType === 'commodity' &&
      !this.commodity &&
      this.searchTerm
    ) {
      this.commodity = this.searchTerm;
    }
  }

  get isFormValid(): boolean {
    switch (this.entityType) {
      case 'company':
        return !!this.pendingCompanyData;
      case 'topic':
        return this.topic.trim().length > 0;
      case 'commodity':
        return this.commodity.trim().length > 0;
    }
  }

  onNavigateToEntity(entity: { type: string; id: string }): void {
    this.dialog.closeAll();
    this.aiSearchService.navigateToEntity(entity);
  }

  onSearchStateChange(state: { enabled: boolean; enriching: boolean }): void {
    const wasEnriching = this.companyEnriching;
    this.companySearchEnabled = state.enabled;
    this.companyEnriching = state.enriching;
    if (state.enriching && !wasEnriching) {
      this.loadingSubject = this.companyFormRef?.legalName || this.searchTerm;
    }
  }

  cancelLoading(): void {
    this.generationSub?.unsubscribe();
    this.generationSub = null;
    // Closing the dialog destroys the company form, which aborts an
    // in-flight enrichment via its takeUntil(destroy$).
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.generationSub?.unsubscribe();
  }

  triggerCompanySearch(): void {
    this.companyFormRef?.onSearch();
  }

  onCompanySubmit(data: CompanyFormData): void {
    this.pendingCompanyData = data;
    this.generateQuery();
  }

  generateQuery(): void {
    if (!this.isFormValid) {
      return;
    }

    this.step = 'loading';
    this.errorMessage = '';

    let request$: Observable<LexiconResponse>;

    switch (this.entityType) {
      case 'company': {
        if (!this.pendingCompanyData) return;
        this.loadingSubject = this.pendingCompanyData.request.legal_name;
        request$ = this.aiSearchService.generateCompanySearch(
          this.pendingCompanyData,
        );
        this.pendingCompanyData = null;
        break;
      }
      case 'topic': {
        this.loadingSubject = this.topic.trim();
        const request: TopicQueryRequest = {
          topic: this.topic.trim(),
          languages: this.aiSearchService.getLanguages(),
        };
        if (this.entityValue) request.entity = this.entityValue;

        request$ = this.aiSearchService.generateTopicQuery(request);
        break;
      }
      case 'commodity': {
        this.loadingSubject = this.commodity.trim();
        const request: CommodityQueryRequest = {
          commodity: this.commodity.trim(),
          languages: this.aiSearchService.getLanguages(),
        };
        request$ = this.aiSearchService.generateCommodityQuery(request);
        break;
      }
    }

    this.generationSub = request$.subscribe({
      next: (response) => {
        this.dialog.closeAll();
        const query = this.setEntityFilter(response.search_query.query);
        const queryName = this.setQueryName(response, query);
        const dialogData = {
          name: queryName,
          description: truncateDescription(response.search_query.description),
          query,
          groups: [] as unknown[],
          _isAiGenerated: true as const,
        };
        this.aiRecentsService.add(dialogData);
        this.aiSearchService.launchAiSearch(dialogData);
      },
      error: (err) => {
        this.step = 'configure';
        // A timed-out generation carries no API detail.
        this.errorMessage =
          err?.name === 'TimeoutError'
            ? 'This is taking longer than expected. Please try again.'
            : err?.error?.detail ||
              'Failed to generate query. Please try again.';
      },
    });
  }

  private setEntityFilter(query: YukkaQuery): YukkaQuery {
    const controlVal = this.entityControl.value as Record<string, unknown>;
    const entityName = controlVal?.['entity']
      ? (controlVal['entity'] as Entity)['name']
      : '';

    if (!entityName) return query;

    const patchFilter = (f: QueryFilter): QueryFilter =>
      f.field === 'entity' && !f.name
        ? { ...f, name: entityName, input: entityName }
        : f;

    return {
      ...query,
      filters: (query.filters || []).map(patchFilter),
    };
  }

  private setQueryName(
    searchQuery: LexiconResponse,
    query: YukkaQuery,
  ): string {
    const capitalize = (s: string): string =>
      s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    const topic = capitalize(searchQuery.lexicon.base_topic || '');
    const entityName =
      query.filters.find((f) => f.field === 'entity')?.name || '';

    switch (this.entityType) {
      case 'company':
        return searchQuery.search_query.name;
      case 'topic':
        return entityName ? `${topic} - ${entityName}` : topic;
      case 'commodity':
        return searchQuery.lexicon.name;
    }
  }
}
